import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function BookingResult() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking" });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "failed" });
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/bookings/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "success", data });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ status: "failed", data });
          return;
        }
        attempts += 1;
        if (attempts >= 8) {
          setState({ status: "pending", data });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setState({ status: "failed" });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="page-booking-result" className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-24 pt-20 pb-32">
      <div className="max-w-2xl mx-auto text-center bg-bg-surface border border-edge p-10 md:p-16">
        {state.status === "checking" && (
          <>
            <Loader2 className="animate-spin mx-auto text-brand mb-8" size={48} />
            <h1 className="font-display text-3xl text-ink mb-4">{t("bookingResult.checking")}</h1>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-brand mb-8" size={64} />
            <p className="text-eyebrow text-brand mb-4">Stripe</p>
            <h1 className="font-display text-4xl text-ink mb-6">{t("bookingResult.success")}</h1>
            <p className="text-ink-muted leading-relaxed">{t("bookingResult.successBody")}</p>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Loader2 className="animate-spin mx-auto text-brand mb-8" size={48} />
            <h1 className="font-display text-3xl text-ink mb-4">{t("bookingResult.pending")}</h1>
          </>
        )}
        {state.status === "failed" && (
          <>
            <XCircle className="mx-auto text-bad mb-8" size={64} />
            <h1 className="font-display text-3xl text-ink mb-4">{t("bookingResult.failed")}</h1>
          </>
        )}
        <Link
          to="/"
          data-testid="back-home"
          className="inline-block mt-10 px-7 py-3 border border-edge text-ink hover:border-brand hover:text-brand transition-colors text-sm tracking-wide"
        >
          {t("bookingResult.backHome")}
        </Link>
      </div>
    </div>
  );
}
